import { Link } from 'react-router-dom';
import { ArrowRight, Building, Globe, Handshake, Heart, GraduationCap, Users, Shield, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function Collaborations() {
  const partnerCategories = [
    {
      category: 'Government Partners',
      icon: Building,
      description: 'Working with government institutions to strengthen healthcare systems',
      organizations: [
        { name: 'Ministry of Health, Tanzania', description: 'National health policies and programs implementation' },
        { name: 'Pwani Regional Health Office', description: 'Regional healthcare coordination and support' },
        { name: 'Bagamoyo District Council', description: 'Local health initiatives and community programs' },
      ]
    },
    {
      category: 'International Partners',
      icon: Globe,
      description: 'Collaborating with global health organizations for best practices',
      organizations: [
        { name: 'World Health Organization (WHO)', description: 'Technical guidance and global health standards' },
        { name: 'International Health Partners', description: 'Healthcare development programs and support' },
        { name: 'Medical Aid Organizations', description: 'Emergency response and humanitarian support' },
      ]
    },
    {
      category: 'Academic Partners',
      icon: GraduationCap,
      description: 'Partnering with educational institutions for training and research',
      organizations: [
        { name: 'Medical Training Institutions', description: 'Clinical training and professional development' },
        { name: 'Research Organizations', description: 'Public health research and studies' },
        { name: 'Healthcare Education Bodies', description: 'Continuing medical education programs' },
      ]
    },
    {
      category: 'Community Partners',
      icon: Heart,
      description: 'Engaging with local organizations for community health',
      organizations: [
        { name: 'Community Health Organizations', description: 'Grassroots health initiatives and outreach' },
        { name: 'Local NGOs', description: 'Community development and health education' },
        { name: 'Faith-Based Organizations', description: 'Health awareness and community support' },
      ]
    },
  ];

  const partnershipValues = [
    {
      icon: Handshake,
      title: 'Mutual Benefit',
      description: 'Creating partnerships that benefit all stakeholders and the community we serve'
    },
    {
      icon: Target,
      title: 'Shared Goals',
      description: 'Aligning our efforts toward common objectives of improved healthcare'
    },
    {
      icon: Shield,
      title: 'Trust & Integrity',
      description: 'Building relationships based on transparency and ethical practices'
    },
    {
      icon: Users,
      title: 'Community Focus',
      description: 'Keeping community health at the center of all collaborative efforts'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-20 md:py-28" data-testid="collaborations-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">About Us</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Collaborations
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Through strategic partnerships with government agencies, international organizations, academic institutions, and community stakeholders, we strengthen our capacity to serve the community and advance healthcare delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Philosophy */}
      <section className="section-spacing" data-testid="collaborations-philosophy">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-20 h-20 bg-accent/10 rounded-2xl flex items-center justify-center mb-6">
                <Handshake className="w-10 h-10 text-accent" />
              </div>
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Approach</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                Partnership for Impact
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                At Baobab Hospital, we believe that collaboration is essential to achieving our mission. By working together with diverse partners, we can leverage complementary strengths, share knowledge, and maximize our impact on community health.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our partnerships span various areas including clinical care, medical education, research, community health programs, and capacity building. Together with our partners, we are committed to improving health outcomes for the communities we serve.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We actively seek collaborations with organizations that share our commitment to quality healthcare, community welfare, and sustainable development. Through these partnerships, we aim to expand our reach and deepen our impact across the Pwani region.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-warm p-8 rounded-2xl shadow-card text-center">
                <p className="font-heading text-4xl font-bold text-primary">15+</p>
                <p className="text-slate-600 mt-2">Partner Organizations</p>
              </div>
              <div className="bg-warm p-8 rounded-2xl shadow-card text-center">
                <p className="font-heading text-4xl font-bold text-primary">10+</p>
                <p className="text-slate-600 mt-2">Active Programs</p>
              </div>
              <div className="bg-warm p-8 rounded-2xl shadow-card text-center">
                <p className="font-heading text-4xl font-bold text-primary">5+</p>
                <p className="text-slate-600 mt-2">Countries</p>
              </div>
              <div className="bg-warm p-8 rounded-2xl shadow-card text-center">
                <p className="font-heading text-4xl font-bold text-primary">20+</p>
                <p className="text-slate-600 mt-2">Years of Partnership</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Values */}
      <section className="section-spacing bg-warm" data-testid="collaborations-values">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Values</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Partnership Principles
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These principles guide our approach to building and maintaining successful partnerships.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnershipValues.map((value, index) => (
              <Card key={index} className="bg-white" data-testid={`partnership-value-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-primary text-lg mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="section-spacing" data-testid="collaborations-partners">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Network</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">Our Partners</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We are grateful for the support and collaboration of our valued partners who help us deliver quality healthcare.
            </p>
          </div>

          <div className="space-y-16">
            {partnerCategories.map((category, index) => (
              <div key={index} data-testid={`partner-category-${index}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center">
                    <category.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-primary">{category.category}</h3>
                    <p className="text-slate-600 text-sm">{category.description}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6 pl-18">
                  {category.organizations.map((org, i) => (
                    <Card key={i} className="card-service">
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-primary mb-2">{org.name}</h4>
                        <p className="text-slate-600 text-sm">{org.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Partner */}
      <section className="py-20 bg-accent" data-testid="collaborations-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Become a Partner
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Interested in collaborating with Baobab Hospital? We welcome partnerships that align with our mission to improve community health in the Pwani region.
          </p>
          <Link to="/contact" data-testid="collaborations-contact">
            <Button className="bg-white text-accent hover:bg-slate-100 rounded-full px-8 py-4 font-semibold shadow-lg">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
